import React from 'react';
import {
 BarChart,
 Bar,
 XAxis,
 YAxis,
 CartesianGrid,
 Tooltip,
 Legend,
 ResponsiveContainer,
 Line,
 AreaChart,
 Area,
 PieChart,
 Pie,
 Cell,
 RadarChart,
 Radar,
 PolarGrid,
 PolarAngleAxis,
 PolarRadiusAxis,
} from 'recharts';
import {
 TrendingUp,
 DollarSign,
 Calendar,
 RefreshCw,
 Users,
 Building2,
 CreditCard,
 Target,
 Activity,
 Loader2,
 ListChecks,
 ArrowLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAdminAnalytics } from '../../hooks/useAdminAnalytics';
import CustomSelect from '../../components/shared/CustomSelect';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const COLORS = ['#0EA5E9', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#64748B', '#0284C7'];

const AdminAdvancedAnalytics = () => {
 const {
 analytics,
 isLoading,
 error,
 period,
 setPeriod,
 periodOptions,
 refetch,
 } = useAdminAnalytics('month');

 const formatCurrency = (val) => {
 if (val == null || isNaN(val)) return '$0';
 return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
 };

 const revenueTrendData = (analytics?.trends?.revenue || []).map((t) => ({
 name: t.period,
 revenue: Number(t.revenue || 0),
 }));

 const bookingsTrendData = (analytics?.trends?.bookings || []).map((t) => ({
 name: t.period,
 bookings: Number(t.count || 0),
 }));

 const combinedTrendData = revenueTrendData.length > 0
 ? revenueTrendData.map((r, i) => ({
 name: r.name,
 revenue: r.revenue,
 bookings: (bookingsTrendData[i]?.bookings ?? 0),
 }))
 : bookingsTrendData.length > 0
 ? bookingsTrendData.map((b) => ({
 name: b.name,
 revenue: 0,
 bookings: b.bookings,
 }))
 : [];

 const bookingsByStatusData = analytics?.bookings_analytics?.by_status
 ? Object.entries(analytics.bookings_analytics.by_status).map(([status, count], i) => ({
 name: status.replace(/_/g, ' '),
 value: count,
 color: COLORS[i % COLORS.length],
 }))
 : [];

 const revenueBreakdownData = [
 { name: 'Platform Fees', value: Number(analytics?.revenue_analytics?.platform_fees || 0), color: '#0EA5E9' },
 { name: 'Vendor Payouts', value: Number(analytics?.revenue_analytics?.vendor_payouts || 0), color: '#10B981' },
 { name: 'Processor Fees', value: Number(analytics?.revenue_analytics?.processor_fees || 0), color: '#F59E0B' },
 ].filter((d) => d.value > 0);

 const paymentStatusData = analytics?.payment_analytics
 ? [
 { name: 'Completed', value: analytics.payment_analytics.completed || 0, color: '#10B981' },
 { name: 'Pending', value: analytics.payment_analytics.pending || 0, color: '#F59E0B' },
 { name: 'Failed', value: analytics.payment_analytics.failed || 0, color: '#EF4444' },
 { name: 'Refunded', value: analytics.payment_analytics.refunded || 0, color: '#64748B' },
 ].filter((d) => d.value > 0)
 : [];

 const topVendorsData = (analytics?.top_performers?.top_vendors || []).slice(0, 8).map((v) => ({
 name: (v.business_name || 'Vendor').slice(0, 15),
 revenue: Number(v.revenue || 0),
 bookings: v.booking_count || 0,
 }));

 const topServicesData = (analytics?.top_performers?.top_services || []).slice(0, 8).map((s) => ({
 name: (s.service_name || 'Service').slice(0, 15),
 revenue: Number(s.revenue || 0),
 bookings: s.booking_count || 0,
 }));

 const userTypeData = analytics?.user_analytics?.by_type
 ? Object.entries(analytics.user_analytics.by_type).map(([type, count], i) => ({
 name: type,
 value: count,
 color: COLORS[i % COLORS.length],
 }))
 : [];

 const platformMetricsData = [
 { metric: 'Conversion', value: Number(analytics?.bookings_analytics?.conversion_rate || 0), fullMark: 100 },
 { metric: 'Completion', value: 100 - Number(analytics?.bookings_analytics?.cancellation_rate || 0), fullMark: 100 },
 { metric: 'Payment Success', value: Number(analytics?.payment_analytics?.success_rate || 0), fullMark: 100 },
 { metric: 'Vendor Verified', value: analytics?.vendor_analytics?.total > 0
 ? (analytics.vendor_analytics.verified / analytics.vendor_analytics.total) * 100 : 0, fullMark: 100 },
 ];

 if (error) {
 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Advanced Analytics"
 subtitle="Comprehensive platform analytics with charts and insights"
 />
 <div className="rounded-lg border border-red-200 bg-surface p-6 text-center shadow-sm">
 <p className="text-red-600">Failed to load analytics. Please try again.</p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 className="sm:items-center"
 title="Advanced Analytics"
 subtitle="Comprehensive platform analytics with charts and insights"
 actions={
 <div className="flex flex-nowrap items-center justify-end gap-2 sm:gap-3">
 <Link
 to="/admin/analytics"
 className="inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 px-3.5 py-1.5 text-xs font-medium rounded-md border border-border bg-surface text-ink hover:bg-surface-muted"
 >
 <ArrowLeft className="w-4 h-4 shrink-0" />
 Back to Analytics
 </Link>
 <div className="w-40 shrink-0">
 <CustomSelect
 value={period}
 onChange={setPeriod}
 options={periodOptions}
 size="sm"
 />
 </div>
 <button
 type="button"
 onClick={() => refetch()}
 className="shrink-0 p-2.5 border border-border rounded-md bg-surface text-ink hover:bg-surface-muted"
 aria-label="Refresh analytics"
 >
 <RefreshCw className="w-4 h-4" />
 </button>
 </div>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.advancedAnalytics.title}
 steps={ADMIN_PAGE_GUIDES.advancedAnalytics.steps}
 icon={ListChecks}
 />

 {isLoading ? (
 <div className="rounded-lg border border-border bg-surface shadow-sm py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading advanced analytics…</p>
 </div>
 ) : (
 <>
 <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4">
 <DollarSign className="w-6 h-6 text-emerald-500 mb-2" />
 <p className="text-xs text-ink-tertiary uppercase">Total Revenue</p>
 <p className="text-lg font-bold text-ink tabular-nums">{formatCurrency(analytics?.overview?.total_revenue)}</p>
 </div>
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4">
 <Calendar className="w-6 h-6 text-ink-secondary mb-2" />
 <p className="text-xs text-ink-tertiary uppercase">Bookings</p>
 <p className="text-lg font-bold text-ink tabular-nums">{analytics?.overview?.total_bookings || 0}</p>
 </div>
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4">
 <TrendingUp className="w-6 h-6 text-ink mb-2" />
 <p className="text-xs text-ink-tertiary uppercase">Platform Fees</p>
 <p className="text-lg font-bold text-ink tabular-nums">{formatCurrency(analytics?.overview?.platform_fees)}</p>
 </div>
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4">
 <Users className="w-6 h-6 text-ink mb-2" />
 <p className="text-xs text-ink-tertiary uppercase">Users</p>
 <p className="text-lg font-bold text-ink tabular-nums">{analytics?.user_analytics?.total || 0}</p>
 </div>
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4">
 <Building2 className="w-6 h-6 text-amber-500 mb-2" />
 <p className="text-xs text-ink-tertiary uppercase">Vendors</p>
 <p className="text-lg font-bold text-ink tabular-nums">{analytics?.vendor_analytics?.total || 0}</p>
 </div>
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4">
 <CreditCard className="w-6 h-6 text-emerald-600 mb-2" />
 <p className="text-xs text-ink-tertiary uppercase">Payments</p>
 <p className="text-lg font-bold text-ink tabular-nums">{analytics?.payment_analytics?.total_payments || 0}</p>
 </div>
 </div>

 {combinedTrendData.length > 0 && (
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <TrendingUp className="w-5 h-5 text-gray-500" />
 <h2 className="text-lg font-semibold text-ink">Revenue & Bookings Over Time</h2>
 </div>
 <div className="h-80">
 <ResponsiveContainer width="100%" height="100%">
 <AreaChart data={combinedTrendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
 <defs>
 <linearGradient id="adminColorRevenue" x1="0" y1="0" x2="0" y2="1">
 <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3} />
 <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} />
 </linearGradient>
 </defs>
 <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
 <XAxis dataKey="name" tick={{ fontSize: 12 }} />
 <YAxis yAxisId="left" tickFormatter={(v) => `$${v}`} />
 <YAxis yAxisId="right" orientation="right" />
 <Tooltip
 formatter={(value, name) => [
 name === 'revenue' ? formatCurrency(value) : value,
 name === 'revenue' ? 'Revenue' : 'Bookings',
 ]}
 />
 <Legend />
 <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#0EA5E9" fill="url(#adminColorRevenue)" name="Revenue" strokeWidth={2} />
 <Line yAxisId="right" type="monotone" dataKey="bookings" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} name="Bookings" />
 </AreaChart>
 </ResponsiveContainer>
 </div>
 </div>
 )}

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
 {bookingsByStatusData.length > 0 && (
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <Calendar className="w-5 h-5 text-gray-500" />
 <h2 className="text-lg font-semibold text-ink">Bookings by Status</h2>
 </div>
 <div className="h-72">
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie
 data={bookingsByStatusData}
 cx="50%"
 cy="50%"
 outerRadius={100}
 dataKey="value"
 nameKey="name"
 label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
 >
 {bookingsByStatusData.map((entry, i) => (
 <Cell key={i} fill={entry.color} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </ResponsiveContainer>
 </div>
 </div>
 )}

 {revenueBreakdownData.length > 0 && (
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <DollarSign className="w-5 h-5 text-gray-500" />
 <h2 className="text-lg font-semibold text-ink">Revenue Breakdown</h2>
 </div>
 <div className="h-72">
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie
 data={revenueBreakdownData}
 cx="50%"
 cy="50%"
 innerRadius={60}
 outerRadius={90}
 dataKey="value"
 nameKey="name"
 label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
 >
 {revenueBreakdownData.map((entry, i) => (
 <Cell key={i} fill={entry.color} />
 ))}
 </Pie>
 <Tooltip formatter={(v) => formatCurrency(v)} />
 <Legend />
 </PieChart>
 </ResponsiveContainer>
 </div>
 </div>
 )}
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <Target className="w-5 h-5 text-gray-500" />
 <h2 className="text-lg font-semibold text-ink">Platform Metrics</h2>
 </div>
 <div className="h-72">
 <ResponsiveContainer width="100%" height="100%">
 <RadarChart data={platformMetricsData}>
 <PolarGrid stroke="#E5E7EB" />
 <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
 <PolarRadiusAxis angle={90} domain={[0, 100]} />
 <Radar name="Score %" dataKey="value" stroke="#0EA5E9" fill="#0EA5E9" fillOpacity={0.4} strokeWidth={2} />
 <Tooltip formatter={(v) => [`${Number(v).toFixed(1)}%`, 'Score']} />
 </RadarChart>
 </ResponsiveContainer>
 </div>
 </div>

 {paymentStatusData.length > 0 && (
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <CreditCard className="w-5 h-5 text-gray-500" />
 <h2 className="text-lg font-semibold text-ink">Payment Status</h2>
 </div>
 <div className="h-72">
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie
 data={paymentStatusData}
 cx="50%"
 cy="50%"
 outerRadius={100}
 dataKey="value"
 nameKey="name"
 label={({ name, value }) => `${name}: ${value}`}
 >
 {paymentStatusData.map((entry, i) => (
 <Cell key={i} fill={entry.color} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </ResponsiveContainer>
 </div>
 </div>
 )}
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
 {topVendorsData.length > 0 && (
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <Building2 className="w-5 h-5 text-gray-500" />
 <h2 className="text-lg font-semibold text-ink">Top Vendors by Revenue</h2>
 </div>
 <div className="h-80">
 <ResponsiveContainer width="100%" height="100%">
 <BarChart data={topVendorsData} layout="vertical" margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
 <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
 <XAxis type="number" tickFormatter={(v) => `$${v}`} />
 <YAxis type="category" dataKey="name" width={65} tick={{ fontSize: 11 }} />
 <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
 <Bar dataKey="revenue" fill="#0EA5E9" radius={[0, 4, 4, 0]} name="Revenue" />
 </BarChart>
 </ResponsiveContainer>
 </div>
 </div>
 )}

 {topServicesData.length > 0 && (
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <Activity className="w-5 h-5 text-gray-500" />
 <h2 className="text-lg font-semibold text-ink">Top Services by Revenue</h2>
 </div>
 <div className="h-80">
 <ResponsiveContainer width="100%" height="100%">
 <BarChart data={topServicesData} layout="vertical" margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
 <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
 <XAxis type="number" tickFormatter={(v) => `$${v}`} />
 <YAxis type="category" dataKey="name" width={65} tick={{ fontSize: 11 }} />
 <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
 <Bar dataKey="revenue" fill="#10B981" radius={[0, 4, 4, 0]} name="Revenue" />
 </BarChart>
 </ResponsiveContainer>
 </div>
 </div>
 )}
 </div>

 {userTypeData.length > 0 && (
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-6">
 <div className="flex items-center gap-2 mb-4">
 <Users className="w-5 h-5 text-gray-500" />
 <h2 className="text-lg font-semibold text-ink">Users by Type</h2>
 </div>
 <div className="h-64">
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie
 data={userTypeData}
 cx="50%"
 cy="50%"
 outerRadius={90}
 dataKey="value"
 nameKey="name"
 label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
 >
 {userTypeData.map((entry, i) => (
 <Cell key={i} fill={entry.color} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </ResponsiveContainer>
 </div>
 </div>
 )}

 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-6">
 <h2 className="text-lg font-semibold text-ink mb-4">Detailed Metrics</h2>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
 <div className="p-4 rounded-xl bg-surface-muted/60">
 <p className="text-sm text-ink-secondary">Avg Booking Value</p>
 <p className="text-xl font-bold text-ink tabular-nums">{formatCurrency(analytics?.bookings_analytics?.average_value)}</p>
 </div>
 <div className="p-4 rounded-xl bg-surface-muted/60">
 <p className="text-sm text-ink-secondary">Conversion Rate</p>
 <p className="text-xl font-bold text-ink tabular-nums">{(analytics?.bookings_analytics?.conversion_rate || 0).toFixed(1)}%</p>
 </div>
 <div className="p-4 rounded-xl bg-surface-muted/60">
 <p className="text-sm text-ink-secondary">Cancellation Rate</p>
 <p className="text-xl font-bold text-ink tabular-nums">{(analytics?.bookings_analytics?.cancellation_rate || 0).toFixed(1)}%</p>
 </div>
 <div className="p-4 rounded-xl bg-surface-muted/60">
 <p className="text-sm text-ink-secondary">Payment Success Rate</p>
 <p className="text-xl font-bold text-ink tabular-nums">{(analytics?.payment_analytics?.success_rate || 0).toFixed(1)}%</p>
 </div>
 </div>
 </div>
 </>
 )}
 </div>
 );
};

export default AdminAdvancedAnalytics;
